import React, { useState, useMemo } from "react";
import "../../assets/style/CommonTable.css";
import { FiPlus, FiChevronLeft, FiChevronRight } from "react-icons/fi";

export default function CommonTable({
  data = [],
  columns = [],
  showAddButton = false,
  onAdd = () => {},
  onSelectionModelChange,
  rowsPerPage = 15,
}) {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [highlightColumn, setHighlightColumn] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAll, setShowAll] = useState(false);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
    setHighlightColumn(key);
  };

  const filteredData = useMemo(() => {
    if (showAll || !searchTerm) return data;
    return data.filter((row) =>
      columns.some((col) =>
        row[col.key]
          ?.toString()
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm, columns, showAll]);

  const sortedData = useMemo(() => {
    let sortable = [...filteredData];
    if (sortConfig.key) {
      sortable.sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        if (aValue instanceof Date && bValue instanceof Date) {
          return sortConfig.direction === "ascending"
            ? aValue - bValue
            : bValue - aValue;
        }
        return sortConfig.direction === "ascending"
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      });
    }
    return sortable;
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
  const paginatedData = showAll
    ? sortedData
    : sortedData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
      );

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return { content: "⇅", opacity: 0.3 };
    return {
      content: sortConfig.direction === "ascending" ? "↑" : "↓",
      opacity: 1,
    };
  };

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowAll(!value);
    setCurrentPage(1);
  };

  const handleShowAllChange = (e) => {
    const isChecked = e.target.checked;
    setShowAll(isChecked);
    if (isChecked) {
      setSearchTerm("");
    }
    setCurrentPage(1);
  };

  const generatePaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    items.push(
      <li className="usa-pagination__item" key="first">
        <button
          className={`usa-pagination__button ${
            currentPage === 1 ? "usa-current" : ""
          }`}
          onClick={() => changePage(1)}
          aria-label="Page 1"
        >
          1
        </button>
      </li>
    );

    if (totalPages <= 1) return items;

    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);

    if (endPage - startPage < maxVisiblePages - 3) {
      startPage = Math.max(2, endPage - (maxVisiblePages - 3));
    }

    if (startPage > 2) {
      items.push(
        <li
          className="usa-pagination__item usa-pagination__overflow"
          key="ellipsis1"
        >
          <span>...</span>
        </li>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <li className="usa-pagination__item" key={i}>
          <button
            className={`usa-pagination__button ${
              currentPage === i ? "usa-current" : ""
            }`}
            onClick={() => changePage(i)}
            aria-label={`Page ${i}`}
          >
            {i}
          </button>
        </li>
      );
    }

    if (endPage < totalPages - 1) {
      items.push(
        <li
          className="usa-pagination__item usa-pagination__overflow"
          key="ellipsis2"
        >
          <span>...</span>
        </li>
      );
    }

    if (totalPages > 1) {
      items.push(
        <li className="usa-pagination__item" key="last">
          <button
            className={`usa-pagination__button ${
              currentPage === totalPages ? "usa-current" : ""
            }`}
            onClick={() => changePage(totalPages)}
            aria-label={`Page ${totalPages}`}
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return items;
  };

  return (
    <div className="authors-table-container">
      <div className="authors-table-header display-flex flex-justify-between flex-align-center flex-wrap margin-bottom-2">
        <div className="display-flex flex-align-center flex-wrap flex-justify-between width-full">
          <div className="display-flex flex-align-center flex-wrap">
            <div className="search-wrapper">
              <span className="search-icon">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.415l-3.85-3.85zm-5.242 1.656a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z" />
                </svg>
              </span>
              <input
                type="search"
                className="usa-input custom-search-input"
                placeholder="Search here..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>

            {showAddButton && (
              <button type="button" className="add-new-button" onClick={onAdd}>
                <FiPlus style={{ marginRight: "4px", fontSize: "14px" }} />
                Add New
              </button>
            )}
          </div>

          <div className="show-all-wrapper">
            <label className="usa-checkbox margin-left-2">
              <input
                type="checkbox"
                className="usa-checkbox__input"
                id="showAll"
                onChange={handleShowAllChange}
                checked={showAll}
              />
              <span className="usa-checkbox__label" htmlFor="showAll">
                Show All
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="table-responsive">
        <table className="authors-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`sortable ${
                    highlightColumn === col.key ? "highlighted-column" : ""
                  }`}
                  onClick={() => col.sortable && requestSort(col.key)}
                  style={col.style}
                >
                  {col.label}{" "}
                  {col.sortable && (
                    <span className="sort-icon">
                      {getSortIcon(col.key).content}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row) => (
                <tr key={row.id}>
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={
                        highlightColumn === col.key ? "highlighted-column" : ""
                      }
                    >
                      {col.renderCell
                        ? col.renderCell(row)
                        : col.render
                        ? col.render(row[col.key], row)
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No matching data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!showAll && (
        <nav className="usa-pagination" aria-label="Pagination">
          <ul className="usa-pagination__list">
            <li className="usa-pagination__item usa-pagination__arrow">
              <button
                className="usa-pagination__link usa-pagination__previous-page"
                onClick={() => changePage(currentPage - 1)}
                disabled={currentPage === 1}
                aria-label="Previous page"
              >
                <FiChevronLeft />
                <span className="usa-pagination__link-text">Previous</span>
              </button>
            </li>

            {generatePaginationItems()}

            <li className="usa-pagination__item usa-pagination__arrow">
              <button
                className="usa-pagination__link usa-pagination__next-page"
                onClick={() => changePage(currentPage + 1)}
                disabled={currentPage === totalPages}
                aria-label="Next page"
              >
                <span className="usa-pagination__link-text">Next</span>
                <FiChevronRight />
              </button>
            </li>
          </ul>
        </nav>
      )}
    </div>
  );
}
